import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { formatDueDateForDisplay, type UserRegionalPrefs } from '@/utils/dateTime';
import { useUserRegionalPrefs } from '@/hooks/useUserRegionalPrefs';
import EmptyState from '@/components/common/EmptyState';
import {
  FiAlertCircle,
  FiArchive,
  FiCalendar,
  FiChevronDown,
  FiChevronUp,
  FiClock,
  FiEdit2,
  FiFile,
  FiFlag,
  FiMessageSquare,
  FiPlus,
  FiRefreshCcw,
  FiUser,
  FiUserMinus,
} from 'react-icons/fi';
import { RiFolderTransferLine } from 'react-icons/ri';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatar?: string;
}

interface HistoryEntry {
  id: string;
  action: string;
  field?: string;
  oldValue?: string;
  newValue?: string;
  metadata?: any;
  createdAt: string;
  user: User;
}

interface ActivityTimelineProps {
  taskId: string;
  history: HistoryEntry[];
  loading?: boolean;
}

const getActionIcon = (action: string) => {
  switch (action) {
    case 'CREATED':
      return <FiPlus className="w-3.5 h-3.5" />;
    case 'UPDATED':
    case 'TITLE_UPDATED':
    case 'DESCRIPTION_UPDATED':
      return <FiEdit2 className="w-3.5 h-3.5" />;
    case 'COMMENTED':
      return <FiMessageSquare className="w-3.5 h-3.5" />;
    case 'ASSIGNED':
      return <FiUser className="w-3.5 h-3.5" />;
    case 'UNASSIGNED':
      return <FiUserMinus className="w-3.5 h-3.5" />;
    case 'STATUS_CHANGED':
      return <FiFlag className="w-3.5 h-3.5" />;
    case 'PRIORITY_CHANGED':
      return <FiAlertCircle className="w-3.5 h-3.5" />;
    case 'DUE_DATE_CHANGED':
      return <FiCalendar className="w-3.5 h-3.5" />;
    case 'SPRINT_CHANGED':
      return <RiFolderTransferLine className="w-3.5 h-3.5" />;
    case 'ATTACHMENT_ADDED':
    case 'ATTACHMENT_REMOVED':
      return <FiFile className="w-3.5 h-3.5" />;
    case 'ARCHIVED':
      return <FiArchive className="w-3.5 h-3.5" />;
    case 'RESTORED':
      return <FiRefreshCcw className="w-3.5 h-3.5" />;
    default:
      return <FiClock className="w-3.5 h-3.5" />;
  }
};

const getActionColor = (action: string): string => {
  switch (action) {
    case 'CREATED':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'COMMENTED':
      return 'bg-cyan-50 text-cyan-700 border-cyan-200';
    case 'ASSIGNED':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'UNASSIGNED':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'STATUS_CHANGED':
      return 'bg-indigo-50 text-indigo-700 border-indigo-200';
    case 'ARCHIVED':
      return 'bg-rose-50 text-rose-700 border-rose-200';
    case 'RESTORED':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    default:
      return 'bg-gray-50 text-gray-700 border-gray-200';
  }
};

const STATUS_LABELS: Record<string, string> = {
  TODO: 'To Do',
  IN_PROGRESS: 'In Progress',
  IN_REVIEW: 'In Review',
  READY_FOR_TESTING: 'Ready for Testing',
  NEEDS_ATTENTION: 'Needs Attention',
  DONE: 'Done',
  BLOCKED: 'Blocked',
};

const formatStatusValue = (val: string | undefined | null): string => {
  if (!val) return 'none';
  return STATUS_LABELS[val] ?? val;
};

const getActionLabel = (entry: HistoryEntry, prefs?: UserRegionalPrefs): string => {
  const { action, field, oldValue, newValue, metadata } = entry;

  switch (action) {
    case 'CREATED':
      return 'created this task';
    case 'TITLE_UPDATED':
      return 'updated the title';
    case 'DESCRIPTION_UPDATED':
      return 'updated the description';
    case 'STATUS_CHANGED':
      return `changed status from ${formatStatusValue(oldValue)} to ${formatStatusValue(newValue)}`;
    case 'PRIORITY_CHANGED':
      return `changed priority from ${oldValue || 'none'} to ${newValue}`;
    case 'ASSIGNED':
      if (!oldValue) return 'assigned this task';
      return 'reassigned this task';
    case 'UNASSIGNED':
      return 'unassigned this task';
    case 'DUE_DATE_CHANGED':
      return `changed due date from ${formatDueDateForDisplay(oldValue, prefs)} to ${formatDueDateForDisplay(newValue, prefs)}`;
    case 'SPRINT_CHANGED':
      return `moved to ${newValue || 'backlog'}`;
    case 'COMMENTED':
      return 'added a comment';
    case 'ATTACHMENT_ADDED':
      return `added attachment: ${metadata?.filename || 'file'}`;
    case 'ATTACHMENT_REMOVED':
      return `removed attachment: ${metadata?.filename || 'file'}`;
    case 'ARCHIVED':
      return 'archived this task';
    case 'RESTORED':
      return 'restored this task';
    case 'UPDATED':
      return field ? `updated ${field}` : 'updated this task';
    default:
      return `performed ${action.toLowerCase()}`;
  }
};

const ActivityTimeline: React.FC<ActivityTimelineProps> = ({ history, loading }) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const { prefs } = useUserRegionalPrefs();

  const toggleExpand = (id: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const getUserDisplayName = (user: User): string => {
    return [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;
  };

  if (loading) {
    return (
      <div className="p-4 text-center text-xs text-gray-500">Loading activity...</div>
    );
  }

  if (!history || history.length === 0) {
    return <EmptyState title="No activity yet" description="Actions on tasks will appear here" />;
  }

  return (
    <div className="py-1 pr-1">
      <div className="space-y-2">
        {history.map((entry, index) => {
          const isExpanded = expandedItems.has(entry.id);
          const hasDetails = !!(entry.oldValue || entry.newValue || entry.metadata?.comment);

          return (
            <div key={entry.id} className="relative pl-7">
              {index < history.length - 1 && (
                <div className="absolute left-[14px] top-7 h-[calc(100%-20px)] w-px bg-gray-200" />
              )}

              <div className={`absolute left-0 top-1 flex h-7 w-7 items-center justify-center rounded-full border ${getActionColor(entry.action)}`}>
                {getActionIcon(entry.action)}
              </div>

              <div className="rounded-md border border-gray-200 bg-white p-2.5">
                <div className="mb-1 flex items-center gap-2">
                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-gray-700 text-[10px] font-semibold text-white">
                    {(entry.user.firstName?.[0] || entry.user.email?.[0] || 'U').toUpperCase()}
                  </div>
                  <span className="text-[11px] font-semibold text-gray-800">{getUserDisplayName(entry.user)}</span>
                  <span className="text-[10px] text-gray-500">{formatDistanceToNow(new Date(entry.createdAt), { addSuffix: true })}</span>
                </div>

                <div className="flex items-start justify-between gap-2">
                  <p className="text-[11px] text-gray-700">{getActionLabel(entry, prefs)}</p>
                  {hasDetails && (
                    <button
                      type="button"
                      onClick={() => toggleExpand(entry.id)}
                      className="rounded p-0.5 text-gray-500 hover:bg-gray-100"
                    >
                      {isExpanded ? <FiChevronUp className="h-3.5 w-3.5" /> : <FiChevronDown className="h-3.5 w-3.5" />}
                    </button>
                  )}
                </div>

                {hasDetails && isExpanded && (
                  <div className="mt-2 border-l-2 border-gray-200 pl-2">
                    {entry.oldValue && (
                      <div className="mb-1 text-[10px]">
                        <span className="text-gray-500">From:</span>{' '}
                        <span className="rounded border border-gray-300 bg-gray-50 px-1.5 py-0.5 text-gray-700">
                          {entry.action === 'DUE_DATE_CHANGED'
                            ? formatDueDateForDisplay(entry.oldValue)
                            : entry.oldValue}
                        </span>
                      </div>
                    )}
                    {entry.newValue && (
                      <div className="mb-1 text-[10px]">
                        <span className="text-gray-500">To:</span>{' '}
                        <span className="rounded border border-blue-300 bg-blue-50 px-1.5 py-0.5 text-blue-700">
                          {entry.action === 'DUE_DATE_CHANGED'
                            ? formatDueDateForDisplay(entry.newValue)
                            : entry.newValue}
                        </span>
                      </div>
                    )}
                    {entry.metadata?.comment && (
                      <div className="mt-1 rounded bg-gray-50 p-2 text-[10px] text-gray-700">{entry.metadata.comment}</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ActivityTimeline;
